/**
 * Auditor's Report — company-type metadata and applicability logic.
 *
 * The prototype's four company-type variants drive which statutory clauses,
 * annexures, and report sections apply. TYPE_META is the single source of
 * truth — the page, the registry tab predicates, and the content builders all
 * read it.
 */
import { escapeHtml } from '../../../utils/draftUtils';
import { determineSmallCompany } from '../../../utils/agmDeadlineEngine';

// Left-panel company-type pills (prototype "1. Company Type").
export const COMPANY_TYPE_OPTIONS = [
  { value: 'pvt', label: 'Pvt. Ltd.' },
  { value: 'public', label: 'Public (Unlisted)' },
  { value: 'listed', label: 'Listed Co.' },
  { value: 'small', label: 'Small / OPC' },
];

// Reporting-framework options (prototype "Reporting Framework").
export const FRAMEWORK_OPTIONS = [
  { value: 'AS', label: 'Accounting Standards (AS) — Rule 7, Cos. (Accounts) Rules 2014' },
  { value: 'IndAS', label: 'Indian Accounting Standards (Ind AS) — Cos. (Ind AS) Rules 2015' },
];

/**
 * Per-type applicability matrix.
 *   kamReq   — SA 701 Key Audit Matters mandatory (listed companies)
 *   caro     — CARO 2020 (Annexure A) applies
 *   ifc      — IFC reporting u/s 143(3)(i) (Annexure B) applies
 *   sec197   — Section 197(16) managerial-remuneration reporting applies
 *   opcSmall — Small Company / OPC (drives exemption wording)
 */
export const TYPE_META = {
  pvt: {
    name: 'Private Limited Company',
    kamReq: false, caro: true, ifc: true, sec197: false, opcSmall: false,
    description:
      'CARO 2020 applies unless private-company exemption thresholds are met '
      + '(paid-up capital & reserves ≤ ₹1 Cr, borrowings ≤ ₹1 Cr, revenue ≤ ₹10 Cr). '
      + 'SA 701 (KAM) is NOT mandatory. IFC reporting u/s 143(3)(i) applies subject to '
      + 'MCA exemption thresholds. Section 197(16) not applicable.',
    applicability: [
      { label: 'CARO 2020', state: 'cond', value: 'Conditional' },
      { label: 'SA 701 (KAM)', state: 'no', value: 'Not required' },
      { label: 'IFC 143(3)(i)', state: 'cond', value: 'Conditional' },
      { label: 'Section 197(16)', state: 'no', value: 'N/A' },
    ],
    note: 'Exempt if thresholds met — CARO: paid-up + reserves ≤ ₹1 Cr, borrowings ≤ ₹1 Cr, revenue ≤ ₹10 Cr; IFC per MCA notification.',
  },
  public: {
    name: 'Public Limited (Unlisted) Company',
    kamReq: false, caro: true, ifc: true, sec197: true, opcSmall: false,
    description:
      'CARO 2020 applies in full. IFC reporting under Section 143(3)(i) is mandatory. '
      + 'Section 197(16) reporting on managerial remuneration is mandatory. SA 701 (KAM) '
      + 'is not mandatory but recommended if material judgments exist.',
    applicability: [
      { label: 'CARO 2020', state: 'yes', value: 'Applies' },
      { label: 'SA 701 (KAM)', state: 'cond', value: 'Recommended' },
      { label: 'IFC 143(3)(i)', state: 'yes', value: 'Mandatory' },
      { label: 'Section 197(16)', state: 'yes', value: 'Applies' },
    ],
    note: 'SA 701 (KAM) recommended where material judgments exist.',
  },
  listed: {
    name: 'Listed Company',
    kamReq: true, caro: true, ifc: true, sec197: true, opcSmall: false,
    description:
      'ALL provisions apply: CARO 2020, IFC u/s 143(3)(i), Section 197(16), and SA 701 '
      + '(Key Audit Matters) is MANDATORY. SA 720 (Revised) Other Information reporting '
      + 'is also mandatory.',
    applicability: [
      { label: 'CARO 2020', state: 'yes', value: 'Applies' },
      { label: 'SA 701 (KAM)', state: 'must', value: 'Mandatory' },
      { label: 'IFC 143(3)(i)', state: 'yes', value: 'Applies' },
      { label: 'Section 197(16)', state: 'yes', value: 'Applies' },
    ],
    note: 'SA 720 (Revised) Other Information reporting also mandatory.',
  },
  small: {
    name: 'Small Company / One Person Company',
    kamReq: false, caro: false, ifc: false, sec197: false, opcSmall: true,
    description:
      'CARO 2020 does NOT apply (Para 1(2)(iv) & (v) of CARO 2020). IFC reporting u/s '
      + '143(3)(i) is EXEMPT vide MCA Notification dated 13.06.2017. Section 197 does not '
      + 'apply. A simpler report format is permissible.',
    applicability: [
      { label: 'CARO 2020', state: 'no', value: 'Exempt' },
      { label: 'SA 701 (KAM)', state: 'no', value: 'Not required' },
      { label: 'IFC 143(3)(i)', state: 'no', value: 'Exempt' },
      { label: 'Section 197(16)', state: 'no', value: 'N/A' },
    ],
    note: 'Simpler report format permissible (CARO Para 1(2)(iv)&(v); IFC exempt per MCA 13.06.2017).',
  },
};

export const getTypeMeta = (typeKey) => TYPE_META[typeKey] || TYPE_META.pvt;

/**
 * Structured statutory-applicability rows for the company-type panel matrix.
 * `rows` carry a color `state` (yes | must | cond | no) + a short `value`
 * label; `note` holds the residual threshold/exemption conditions.
 */
export function applicabilityMatrix(typeKey) {
  const m = getTypeMeta(typeKey);
  return { name: m.name, rows: m.applicability || [], note: m.note || '' };
}

export const ifcApplicable = (config) => getTypeMeta(config?.companyType).ifc;

// ── CARO 2020 applicability — paragraph 1(2) of the Order ───────────────────
//
// Section 143(11) read with the Companies (Auditor's Report) Order, 2020: the
// Order applies to every company audited under the Act, INCLUDING a foreign
// company, EXCEPT —
//   (i)   a banking company (Banking Regulation Act, 1949, s.5(c));
//   (ii)  an insurance company (Insurance Act, 1938);
//   (iii) a company licensed under Section 8;
//   (iv)  a One Person Company, and a SMALL company under Sec 2(85) — which,
//         since the 2025 Definitions Rules, means a private company with
//         paid-up capital ≤ ₹10 crore and turnover ≤ ₹100 crore;
//   (v)   a private company — not a subsidiary or holding company of a public
//         company — that satisfies ALL THREE at once: paid-up capital plus
//         reserves and surplus ≤ ₹1 crore at the balance-sheet date, total
//         borrowings from banks or financial institutions ≤ ₹1 crore at any
//         time in the year, and total revenue (Schedule III, including
//         discontinuing operations) ≤ ₹10 crore for the year.
//
// The report used to take CARO from the company-type pill alone, so a private
// company got Annexure A whether or not it was exempt. Now the report decides
// from the facts (client ask, 21 Sep 2026): exempt → the main report states
// that CARO is not applicable and why, and Annexure A is not produced; applies
// → Annexure A as before; undecided (a figure missing) → CARO is INCLUDED, the
// conservative choice, with the studio asking for what would settle it. The
// auditor can always override in either direction and the report says so.

export const CARO_LIMITS = Object.freeze({
  capitalAndReserves: 10000000,   // ₹1 crore
  borrowings: 10000000,           // ₹1 crore
  revenue: 100000000,             // ₹10 crore
});

export const CARO_SECTOR_OPTIONS = [
  { value: 'none', label: 'Ordinary company' },
  { value: 'banking', label: 'Banking company' },
  { value: 'insurance', label: 'Insurance company' },
  { value: 'sec8', label: 'Section 8 company' },
];

export const CARO_OVERRIDE_OPTIONS = [
  { value: 'auto', label: 'Decide from the facts' },
  { value: 'applies', label: 'CARO applies' },
  { value: 'exempt', label: 'CARO not applicable' },
];

const inr = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(String(v).replace(/[^0-9.]/g, ''));
  return Number.isFinite(n) && n >= 0 ? n : null;
};

const crore = (n) => (n === null ? 'not given' : `₹${(n / 10000000).toLocaleString('en-IN', { maximumFractionDigits: 2 })} crore`);

/**
 * @returns {{ applies: boolean|null, exemption: string|null, reason: string, missing: string[], overridden: boolean }}
 */
export function determineCaroApplicability(config = {}) {
  const override = config.caroOverride || 'auto';
  if (override === 'applies') return { applies: true, exemption: null, reason: 'Set by the auditor: CARO 2020 applies.', missing: [], overridden: true };
  if (override === 'exempt') return { applies: false, exemption: 'auditor', reason: 'Set by the auditor: CARO 2020 is not applicable.', missing: [], overridden: true };

  const type = config.companyType || 'pvt';
  const sector = config.caroSector || 'none';
  const done = (applies, exemption, reason, missing = []) => ({ applies, exemption, reason, missing, overridden: false });

  if (sector === 'banking') return done(false, 'banking', 'A banking company as defined in the Banking Regulation Act, 1949 — excluded by paragraph 1(2)(i) of the Order.');
  if (sector === 'insurance') return done(false, 'insurance', 'An insurance company as defined in the Insurance Act, 1938 — excluded by paragraph 1(2)(ii) of the Order.');
  if (sector === 'sec8') return done(false, 'sec8', 'A company licensed to operate under Section 8 of the Act — excluded by paragraph 1(2)(iii) of the Order.');

  if (type === 'small') return done(false, config.caroIsOpc ? 'opc' : 'small_company', config.caroIsOpc
    ? 'A One Person Company — excluded by paragraph 1(2)(iv) of the Order.'
    : 'A small company under Section 2(85) of the Act (paid-up capital not exceeding ₹10 crore and turnover not exceeding ₹100 crore) — excluded by paragraph 1(2)(iv) of the Order.');
  if (config.caroSmallCompany === true) return done(false, 'small_company', 'A small company under Section 2(85) of the Act (paid-up capital not exceeding ₹10 crore and turnover not exceeding ₹100 crore) — excluded by paragraph 1(2)(iv) of the Order.');

  if (type === 'listed' || type === 'public') return done(true, null, `A ${type === 'listed' ? 'listed' : 'public'} company: none of the exclusions in paragraph 1(2) of the Order is available.`);

  // Private company: the three-limb test in paragraph 1(2)(v).
  if (config.caroHoldingOrSubsidiaryOfPublic === true) {
    return done(true, null, 'A private company that is a holding or subsidiary company of a public company cannot claim the private-company exclusion in paragraph 1(2)(v); CARO 2020 applies.');
  }
  const capital = inr(config.caroPaidUpPlusReservesInr);
  const borrowings = inr(config.caroBorrowingsInr);
  const revenue = inr(config.caroTotalRevenueInr);
  const over = [];
  if (capital !== null && capital > CARO_LIMITS.capitalAndReserves) over.push(`paid-up capital and reserves ${crore(capital)} exceeds ₹1 crore`);
  if (borrowings !== null && borrowings > CARO_LIMITS.borrowings) over.push(`borrowings from banks or financial institutions ${crore(borrowings)} exceeded ₹1 crore during the year`);
  if (revenue !== null && revenue > CARO_LIMITS.revenue) over.push(`total revenue ${crore(revenue)} exceeds ₹10 crore`);
  if (over.length) return done(true, null, `A private company outside the paragraph 1(2)(v) exclusion: ${over.join('; ')}. CARO 2020 applies.`);

  const missing = [];
  if (capital === null) missing.push('paid-up capital plus reserves and surplus at the balance-sheet date');
  if (borrowings === null) missing.push('highest borrowings from banks or financial institutions during the year');
  if (revenue === null) missing.push('total revenue for the year');
  if (config.caroHoldingOrSubsidiaryOfPublic !== false) missing.push('whether the company is a holding or subsidiary of a public company');
  if (missing.length) {
    return done(null, null, `A private company; whether the paragraph 1(2)(v) exclusion applies cannot be settled without: ${missing.join('; ')}. CARO 2020 is included until it is.`, missing);
  }
  return done(false, 'private_three_limb', `A private company that is neither a holding nor a subsidiary of a public company, with paid-up capital and reserves of ${crore(capital)} (limit ₹1 crore), borrowings from banks and financial institutions not exceeding ${crore(borrowings)} at any time during the year (limit ₹1 crore) and total revenue of ${crore(revenue)} (limit ₹10 crore) — excluded by paragraph 1(2)(v) of the Order.`);
}

/** Annexure A is produced unless the facts (or the auditor) say CARO does not apply. */
export const caroApplicable = (config) => determineCaroApplicability(config).applies !== false;

/** The sentence the main report carries when Annexure A is not produced. */
export function caroNotApplicableStatement(config) {
  const d = determineCaroApplicability(config);
  if (d.applies !== false) return '';
  const why = {
    banking: 'the Company is a banking company',
    insurance: 'the Company is an insurance company',
    sec8: 'the Company is licensed to operate under Section 8 of the Act',
    opc: 'the Company is a One Person Company',
    small_company: 'the Company is a small company as defined in clause (85) of Section 2 of the Act',
    private_three_limb: 'the Company is a private company, not being a subsidiary or holding company of a public company, whose paid-up capital and reserves and surplus did not exceed one crore rupees as on the balance sheet date, whose total borrowings from banks and financial institutions did not exceed one crore rupees at any point of time during the financial year, and whose total revenue did not exceed ten crore rupees during the financial year',
    auditor: 'the Company falls within the exclusions specified in paragraph 1(2) of the Order',
  }[d.exemption] || 'the Company falls within the exclusions specified in paragraph 1(2) of the Order';
  return `The Companies (Auditor's Report) Order, 2020 ("the Order"), issued by the Central Government of India in terms of sub-section (11) of Section 143 of the Act, is not applicable to the Company since ${why}. Accordingly, no statement on the matters specified in paragraphs 3 and 4 of the Order is given.`;
}

/**
 * Best-effort map from a normalised company record to a prototype company
 * type. `listed` is a separate company field ("Yes — BSE", etc.); `type` /
 * `classOfCompany` carry OPC / Public hints. Defaults to private limited.
 */
export function mapCompanyToType(companyContext = {}) {
  const listed = String(companyContext.listed || '').trim().toLowerCase();
  if (listed.startsWith('yes')) return 'listed';
  const type = String(companyContext.type || '').toLowerCase();
  const cls = String(companyContext.classOfCompany || '').toLowerCase();
  if (type.includes('opc') || type.includes('one person') || cls.includes('one person')) return 'small';
  if (type.includes('public') || cls.includes('public')) return 'public';
  // A private company the record shows to be small under Sec 2(85) takes the
  // Small / OPC variant: no CARO, IFC exemption wording, abridged reporting.
  if (determineSmallCompany(companyContext).small === true) return 'small';
  return 'pvt';
}

/**
 * CARO facts the company record can supply, so the studio opens with the
 * determination as far along as the record allows and asks only for the rest.
 */
export function deriveCaroFactsFromCompany(companyContext = {}) {
  const type = String(companyContext.type || '').toLowerCase();
  const cls = String(companyContext.classOfCompany || '').toLowerCase();
  const isOpc = type.includes('opc') || type.includes('one person') || cls.includes('one person');
  const small = determineSmallCompany(companyContext);
  const holding = companyContext.isHoldingCompany;
  const subsidiary = companyContext.isSubsidiaryCompany;
  const tri = (v) => (v === true || v === 'true' || v === 'yes' ? true : v === false || v === 'false' || v === 'no' ? false : null);
  const h = tri(holding);
  const s = tri(subsidiary);
  return {
    caroIsOpc: isOpc,
    caroSmallCompany: small.small === true,
    caroSector: type.includes('section 8') || type.includes('sec 8') || cls.includes('section 8') ? 'sec8' : 'none',
    // "Of a public company" is not on the record; a known holding/subsidiary
    // flag is treated as blocking the private exclusion (the safe direction),
    // a known-false pair clears it, anything else stays to be answered.
    caroHoldingOrSubsidiaryOfPublic: h === true || s === true ? true : h === false && s === false ? false : null,
    caroPaidUpPlusReservesInr: '',
    caroBorrowingsInr: '',
    caroTotalRevenueInr: '',
    caroOverride: 'auto',
  };
}

/**
 * Placeholder helper — renders a filled value (HTML-escaped) or a highlighted
 * `.doc-ph` placeholder span when the value is empty. Shared by every builder.
 */
export function ph(value, label) {
  const v = value == null ? '' : String(value).trim();
  if (v) return escapeHtml(v);
  return `<span class="doc-ph">${escapeHtml(label)}</span>`;
}

/** Escape + convert newlines to <br> — for AI-supplied free text. */
export function richText(value) {
  return escapeHtml(String(value == null ? '' : value)).replace(/\n/g, '<br>');
}
